import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, OverlayTrigger, Popover } from 'react-bootstrap';
import { ElevenLabsClient } from 'elevenlabs';
import MicRecorder from './MicRecorder';
import { Testimonials } from './Testimonials';
import VoiceControl from './VoiceControl';
import { stockVoices, getFirstStockVoice } from './StockVoices';

// Utility function to shuffle an array with a seed for consistent randomization
const shuffleArray = (array) => {
  // Get the node number from localStorage to use as seed
  const nodeNumber = localStorage.getItem("nn-node");
  
  if (!nodeNumber || array.length === 0) {
    return array;
  }
  
  // Create a copy of the array to avoid mutating the original
  const shuffled = [...array];
  
  // Convert node number to a numeric seed
  const seed = nodeNumber.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  // Better seeded random number generator using xorshift
  let state = seed;
  const seededRandom = () => {
    state ^= state << 13;
    state ^= state >> 17;
    state ^= state << 5;
    return (state >>> 0) / 4294967295; // Convert to 0-1 range
  };
  
  // Fisher-Yates shuffle with seeded random
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled;
};

const VoiceCloneCreatorOptions = (props) => {
  // -----------------------
  // Common API and client setup
  // -----------------------
  // Supply your API key securely (e.g., via an environment variable)
  const apiKey = props.elevenlabsKeys[0]
  const client = useMemo(() => new ElevenLabsClient({ apiKey }), [apiKey]);

  // -----------------------
  // Shared voices state for synthesis dropdown. If you want more default voices, put them in here.
  // -----------------------
  const [voices, setVoices] = useState([
    //{ id: 'YwJoWlHMPeElW7r8zaSX', name: 'Roger' },
  ]);
  const [selectedVoiceId, setSelectedVoiceId] = useState('');

  // -----------------------
  // Section 1: Voice Clone Creation states
  // -----------------------
  const [selectedSource, setSelectedSource] = useState('record'); // 'record' or 'upload'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [cloneResult, setCloneResult] = useState('');
  const [cloneLoading, setCloneLoading] = useState(false);
  const [audioURL, setAudioURL] = useState(null);

  // NEW: Stock voice selection states
  const [useStockVoice, setUseStockVoice] = useState(false);
  const [selectedStockVoice, setSelectedStockVoice] = useState(null);
  const [showStockVoices, setShowStockVoices] = useState(false);

  // NEW: Voice Changer states
  const [showVoiceChanger, setShowVoiceChanger] = useState(false);
  const [voiceChangerSource, setVoiceChangerSource] = useState('reuse'); // 'reuse' or 'record'
  const [voiceChangerAudioURL, setVoiceChangerAudioURL] = useState(null);
  const [selectedVoiceChangerVoice, setSelectedVoiceChangerVoice] = useState(null);
  const [voiceChangerRecordings, setVoiceChangerRecordings] = useState([]);
  const [selectedVoiceChangerRecording, setSelectedVoiceChangerRecording] = useState(null);
  const [voiceChangerResult, setVoiceChangerResult] = useState('');
  const [voiceChangerLoading, setVoiceChangerLoading] = useState(false);
  const [voiceChangerInputs, setVoiceChangerInputs] = useState([]);

  const [showScroll, setShowScroll] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [voiceChangerOutputs, setVoiceChangerOutputs] = useState([]);
  const [synthesisVisible, setSynthesisVisible] = useState(false);
  const [synthesisCompleted, setSynthesisCompleted] = useState(false);
  const [submittedToSlideshow, setSubmittedToSlideshow] = useState(null);
  const [mp3CacheBuster, setMp3CacheBuster] = useState(Date.now());

  // -----------------------
  // Section 2: Synthesis states
  // -----------------------
  // For text-to-speech and speech-to-speech synthesis
  const [audioUrlSynth, setAudioUrlSynth] = useState([]);
  const [synthLoading, setSynthLoading] = useState(false);
  const [error, setError] = useState('');

  // NEW: Synthesis input method state: "text", "record", or "file"
  const [synthInputSource, setSynthInputSource] = useState('text');
  const [synthRecordingURL, setSynthRecordingURL] = useState(null); // from microphone
  const [synthUploadedFile, setSynthUploadedFile] = useState(null);   // from local file
  const [synthesisText, setSynthesisText] = useState('');

  // Set default selected voice for synthesis if none is selected.
  useEffect(() => {
    if (voices.length > 0 && !selectedVoiceId) {
      setSelectedVoiceId(voices[0].id);
    }
  }, [voices, selectedVoiceId]);

  useEffect(() => {
    const checkScroll = () => {
      const scrollPosition = window.pageYOffset;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const isScrollable = documentHeight > windowHeight + 100;
      const shouldShow = isScrollable && (scrollPosition > 200 || scrollPosition + windowHeight < documentHeight - 200);
      setShowScroll(shouldShow);
      setShowScrollBottom(shouldShow);
    };
    window.addEventListener('scroll', checkScroll);
    checkScroll();
    return () => window.removeEventListener('scroll', checkScroll);
  }, []);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const scrollBottom = () => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });

  useEffect(() => {
    // Load all voice-related data from local storage
    const storedVoicesData = localStorage.getItem('voices');
    if (storedVoicesData) {
      const parsedData = JSON.parse(storedVoicesData);
      
      // Load voices
      if (parsedData.voices) {
      console.log("loading voices from local");
        console.log(parsedData.voices);
        setVoices(parsedData.voices);
        if (parsedData.voices.length > 0) {
          setSynthesisVisible(true);
        }
      }
      
      // Load stock voices preference
      if (parsedData.showStockVoices !== undefined) {
        setShowStockVoices(parsedData.showStockVoices);
      }
      
      // Load selected stock voice
      if (parsedData.selectedStockVoice) {
        setSelectedStockVoice(parsedData.selectedStockVoice);
        setUseStockVoice(true);
        setSelectedVoiceId(parsedData.selectedStockVoice.id);
        setSynthesisVisible(true);
      }
      
      // Load synthesis completed state
      if (parsedData.synthesisCompleted) {
        setSynthesisCompleted(parsedData.synthesisCompleted);
        if (parsedData.synthesisCompleted) {
          setShowVoiceChanger(true);
        }
      }
      
      // Load and process synthesized voices
      if (parsedData.synth) {
        let out = []
        for (let i = 0; i < parsedData.synth.length; i++) {
          const base64_blob = parsedData.synth[i]['audio_blob'];
          if (!base64_blob) continue;

          const url = base64ToAudioURL(base64_blob);

        // Add url to array of synth urls
          const new_synth = {'id': parsedData.synth[i]['id'], 'name': parsedData.synth[i]['name'], 'url': url};
        out.push(new_synth);
        console.log(out);
      }
      setAudioUrlSynth(out);
        
        // Load voice changer inputs and outputs separately
        if (parsedData.voiceChangerInputs) {
          // Process input recordings from base64 blobs
          const processedInputs = processAudioRecordingsFromStorage(parsedData.voiceChangerInputs);
          setVoiceChangerInputs(processedInputs);
        }
        
        if (parsedData.voiceChangerOutputs) {
          // Process output recordings from base64 blobs
          const processedOutputs = processAudioRecordingsFromStorage(parsedData.voiceChangerOutputs);
          setVoiceChangerOutputs(processedOutputs);
        }
        
        // Combine inputs and outputs for display if either exists
        if (parsedData.voiceChangerInputs || parsedData.voiceChangerOutputs) {
          setShowVoiceChanger(true);
          const allRecordings = [
            ...(parsedData.voiceChangerInputs || []),
            ...(parsedData.voiceChangerOutputs || [])
          ];
          setVoiceChangerRecordings(allRecordings);
          
          // Auto-select the first recording if none is selected
          if (allRecordings.length > 0 && !selectedVoiceChangerRecording) {
            setSelectedVoiceChangerRecording(allRecordings[0]);
          }
          
          // Auto-select the first target voice if none is selected
          if (stockVoices.length > 0 && !selectedVoiceChangerVoice) {
            setSelectedVoiceChangerVoice(getFirstStockVoice());
          }
        }
        
        // Show voice changer if synthesis has been completed (even without recordings)
        if (synthesisCompleted) {
          setShowVoiceChanger(true);
          // Auto-select the first target voice if none is selected
          if (stockVoices.length > 0 && !selectedVoiceChangerVoice) {
            setSelectedVoiceChangerVoice(getFirstStockVoice());
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    if (props.activeTab === "voice-cloning") {
      console.log("voice tab selected")
      // If we were just selected and there's nothing in the synthesize text box, fill in the elevator pitch
      if (synthesisText === "") {
        setSynthesisText(props.elevatorPitch)
      }
    }
  }, [props.activeTab]);

  function getVoiceNameFromId(id) {
    const voice = voices.find((v) => v.id === id);
    return voice ? voice.name : '';
  }

  // -----------------------
  // Handlers for Clone Creation
  // -----------------------
  const handleFileChange = (e) => {
    setUploadedFile(e.target.files[0]);
  };

  const handleSourceChange = (e) => {
    setSelectedSource(e.target.value);
    // Reset any previous audio sample.
    setAudioURL(null);
    setUploadedFile(null);
  };

  const saveVoicesToLocalStorage = (new_voices) => {
    console.log("saving voices to local!")
    const currentData = localStorage.getItem('voices');
    const existingData = currentData ? JSON.parse(currentData) : {};
    const updatedData = { ...existingData, voices: new_voices };
    try {
      localStorage.setItem('voices', JSON.stringify(updatedData));
    } catch (e) {
      console.error("Failed to save voices to localStorage:", e);
    }
  }

  const saveSynthToLocalStorage = (new_synth) => {
    console.log("saving synth to local!")
    const currentData = localStorage.getItem('voices');
    const existingData = currentData ? JSON.parse(currentData) : {};
    const updatedData = { ...existingData, synth: new_synth };
    try {
      localStorage.setItem('voices', JSON.stringify(updatedData));
    } catch (e) {
      console.error("Failed to save synth to localStorage:", e);
    }
  }

  const saveShowStockVoicesToLocalStorage = (showStock) => {
    console.log("saving showStockVoices to local!")
    const currentData = localStorage.getItem('voices');
    const existingData = currentData ? JSON.parse(currentData) : {};
    const updatedData = { ...existingData, showStockVoices: showStock };
    try {
      localStorage.setItem('voices', JSON.stringify(updatedData));
    } catch (e) {
      console.error("Failed to save showStockVoices to localStorage:", e);
    }
  }

  const saveSelectedStockVoiceToLocalStorage = (stockVoice) => {
    console.log("saving selected stock voice to local!")
    const currentData = localStorage.getItem('voices');
    const existingData = currentData ? JSON.parse(currentData) : {};
    const updatedData = { ...existingData, selectedStockVoice: stockVoice };
    try {
      localStorage.setItem('voices', JSON.stringify(updatedData));
    } catch (e) {
      console.error("Failed to save selectedStockVoice to localStorage:", e);
    }
  }

  const saveSynthesisCompletedToLocalStorage = (completed) => {
    console.log("saving synthesis completed to local!")
    const currentData = localStorage.getItem('voices');
    const existingData = currentData ? JSON.parse(currentData) : {};
    const updatedData = { ...existingData, synthesisCompleted: completed };
    try {
      localStorage.setItem('voices', JSON.stringify(updatedData));
    } catch (e) {
      console.error("Failed to save synthesisCompleted to localStorage:", e);
    }
  }

  const saveVoiceChangerInputsToLocalStorage = (inputRecordings) => {
    console.log("saving voice changer input recordings to local!")
    const currentData = localStorage.getItem('voices');
    const existingData = currentData ? JSON.parse(currentData) : {};
    const updatedData = { ...existingData, voiceChangerInputs: inputRecordings };
    try {
      localStorage.setItem('voices', JSON.stringify(updatedData));
    } catch (e) {
      console.error("Failed to save voiceChangerInputs to localStorage:", e);
    }
  }

  const saveVoiceChangerOutputsToLocalStorage = (outputRecordings) => {
    console.log("saving voice changer output recordings to local!")
    const currentData = localStorage.getItem('voices');
    const existingData = currentData ? JSON.parse(currentData) : {};
    const updatedData = { ...existingData, voiceChangerOutputs: outputRecordings };
    try {
      localStorage.setItem('voices', JSON.stringify(updatedData));
    } catch (e) {
      console.error("Failed to save voiceChangerOutputs to localStorage:", e);
    }
  }

  const handleHideStockVoices = () => {
    setShowStockVoices(false);
    saveShowStockVoicesToLocalStorage(false);
  }

  // NEW: Handler for stock voice selection
  const handleStockVoiceSelect = (voice) => {
    setSelectedStockVoice(voice);
    setUseStockVoice(true);
    // Don't add stock voices to the voices list - they should remain separate
    setSelectedVoiceId(voice.id);
    saveSelectedStockVoiceToLocalStorage(voice);
    
    // Scroll to the voice synthesis section
    setTimeout(() => {
      const synthesisSection = document.querySelector('[style*="visibility: visible"]');
      if (synthesisSection) {
        synthesisSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // NEW: Handler for cloned voice selection
  const handleClonedVoiceSelect = (voiceId) => {
    setSelectedVoiceId(voiceId);
    // Deselect stock voice when selecting a cloned voice
    setSelectedStockVoice(null);
    setUseStockVoice(false);
    saveSelectedStockVoiceToLocalStorage(null);
  };

  // NEW: Handler for voice changer voice selection
  const handleVoiceChangerVoiceSelect = (voice) => {
    setSelectedVoiceChangerVoice(voice);
  };

  // NEW: Handler for submitting to slideshow
  const handleSubmitToSlideshow = async (recording) => {
    console.log('Submitting to slideshow:', recording);
    setSubmittedToSlideshow(recording.id);
    
    try {
      // Fetch the audio blob from the recording URL
      const response = await fetch(recording.url);
      const audioBlob = await response.blob();
      
      // Create FormData to send the audio blob
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice_recording.mp3');
      formData.append('recordingId', recording.id);
      formData.append('recordingName', recording.name);
      formData.append('originalVoice', recording.originalVoice);
      formData.append('targetVoice', recording.targetVoice);
      formData.append('password', localStorage.getItem("frontend-password"));
      formData.append('student_id', localStorage.getItem("nn-node"));
      formData.append('workshop_id', props.workshopId);
      
      // Make POST request to submit voice to slideshow
      const submitResponse = await fetch('/submit-voice-to-slideshow', {
        method: 'POST',
        body: formData
      });
      
      if (!submitResponse.ok) {
        throw new Error(`HTTP error! status: ${submitResponse.status}`);
      }
      
      const result = await submitResponse.json();
      console.log('Voice submitted successfully:', result);
      
    } catch (error) {
      console.error('Error submitting voice to slideshow:', error);
      // Reset the submitted state if there was an error
      setSubmittedToSlideshow(null);
    }
  };

  // NEW: Handler for voice changer button
  const handleVoiceChanger = async () => {
    if (!selectedVoiceChangerVoice || !selectedVoiceChangerRecording) {
      alert('Please select both a recording and a target voice.');
      return;
    }

    setVoiceChangerLoading(true);
    setVoiceChangerResult('');

    console.log(selectedVoiceChangerVoice);

    try {
      // Fetch the audio blob from the recording URL
      const response = await fetch(selectedVoiceChangerRecording.url);
      const audioBlob = await response.blob();
      
      // Convert blob to File object for the API
      const audioFile = new File([audioBlob], 'voice_changer_input.mp3', { type: 'audio/mpeg' });

      // Perform speech-to-speech conversion
      const conversionStream = await client.speechToSpeech.convert(selectedVoiceChangerVoice.id, {
        audio: audioFile,
        outputFormat: "mp3_44100_128",
        modelId: "eleven_english_sts_v2"
      });

      // Collect the audio chunks
      const chunks = [];
      for await (const chunk of conversionStream) {
        chunks.push(chunk);
      }

      // Create the final audio blob and URL
      const convertedAudioBlob = new Blob(chunks, { type: 'audio/mpeg' });
      const convertedAudioURL = URL.createObjectURL(convertedAudioBlob);

      // Convert to base64 for storage
      const convertedAudioBase64 = await blobToBase64(convertedAudioBlob);

      // Add the converted audio to the recordings list
      const newConvertedRecording = {
        id: `converted-${Date.now()}`,
        name: `${voiceChangerOutputs.length + 1}`,
        url: convertedAudioURL,
        audio_blob: convertedAudioBase64,
        originalVoice: selectedVoiceChangerRecording.name,
        targetVoice: selectedVoiceChangerVoice.name
      };

      setVoiceChangerOutputs(prev => {
        const updated = [...prev, newConvertedRecording];
        saveVoiceChangerOutputsToLocalStorage(updated);
        return updated;
      });
      
      // Update combined recordings list
      setVoiceChangerRecordings(prev => {
        const updated = [...prev, newConvertedRecording];
        return updated;
      });
      
      setSelectedVoiceChangerRecording(newConvertedRecording);
      
      setVoiceChangerResult(`Voice changed successfully! Converted from "${selectedVoiceChangerRecording.name}" to "${selectedVoiceChangerVoice.name}"`);
      
    } catch (error) {
      console.error('Error in voice changer:', error);
      setVoiceChangerResult(`Error changing voice: ${error.message}`);
    } finally {
      setVoiceChangerLoading(false);
    }
  };

  // NEW: Handler to add new recording to voice changer list
  const handleAddVoiceChangerRecording = async (newAudioURL) => {
    // Only add if this is a new recording (not already in the list)
    const isNewRecording = !voiceChangerInputs.some(recording => recording.url === newAudioURL);
    
    if (isNewRecording && newAudioURL) {
      // Convert audio URL to base64 blob for storage
      const audioBase64 = await audioURLToBase64(newAudioURL);

      const newRecording = {
        id: `recording-${Date.now()}`,
        name: `${voiceChangerInputs.length + 1}`,
        url: newAudioURL,
        audio_blob: audioBase64
      };
      
      setVoiceChangerInputs(prev => {
        const updated = [...prev, newRecording];
        saveVoiceChangerInputsToLocalStorage(updated);
        return updated;
      });
      
      // Update combined recordings list
      setVoiceChangerRecordings(prev => {
        const updated = [...prev, newRecording];
        return updated;
      });
      
      // Auto-select if this is the only recording
      if (voiceChangerInputs.length === 0) {
        setSelectedVoiceChangerRecording(newRecording);
      }
      // Auto-select new recording if there are multiple recordings
      if (voiceChangerInputs.length > 0) {
        setSelectedVoiceChangerRecording(newRecording);
      }
    }
  };

  const handleCloneSubmit = async (e) => {
    e.preventDefault();
    
    // Generate auto-incrementing voice name
    const voiceNumber = voices.length + 1;
    const voiceName = `Voice Clone ${voiceNumber}`;
    
    let fileForUpload = null;
    let cloneAudioURL = null; // Store the audio URL for voice changer
    let cloneAudioBlob = null; // Store the audio blob for voice changer
    
    if (selectedSource === 'record') {
      if (!audioURL) {
        alert('Please record your voice first.');
        return;
      }
      try {
        const res = await fetch(audioURL);
        const blob = await res.blob();
        fileForUpload = new File([blob], 'voice_sample.webm', { type: blob.type });
        cloneAudioURL = audioURL; // Store the original audio URL
        cloneAudioBlob = blob; // Store the original audio blob
      } catch (error) {
        alert('Error processing recorded audio.');
        console.error('Error fetching blob from audioURL:', error);
        return;
      }
    } else {
      if (!uploadedFile) {
        alert('Please upload an audio file.');
        return;
      }
      fileForUpload = uploadedFile;
      // For uploaded files, create a URL and blob for voice changer
      cloneAudioURL = URL.createObjectURL(uploadedFile);
      cloneAudioBlob = uploadedFile;
    }
    setCloneLoading(true);
    setCloneResult('');
    try {
      const response = await client.voices.add({
        files: [fileForUpload],
        name: voiceName,
      });
      // Create a new voice entry using the returned voice_id and generated voiceName.
      const newVoiceEntry = { id: response.voice_id, name: voiceName };
      console.log("New voice entry:", newVoiceEntry);
      // Update the shared voices state so that the new voice appears in the synthesis dropdown.
      setVoices(prevVoices => {
        const updated = [...prevVoices, newVoiceEntry];
        saveVoicesToLocalStorage(updated);
        return updated;
      });
      
      // Automatically add the clone audio to voice changer recordings
      if (cloneAudioURL && cloneAudioBlob) {
        const audioBase64 = await blobToBase64(cloneAudioBlob);
        
        const newVoiceChangerRecording = {
          id: `clone-recording-${Date.now()}`,
          name: `${voiceNumber}`,
          url: cloneAudioURL,
          audio_blob: audioBase64
        };
        
        setVoiceChangerInputs(prev => {
          const updated = [...prev, newVoiceChangerRecording];
          saveVoiceChangerInputsToLocalStorage(updated);
          return updated;
        });
        
        // Update combined recordings list
        setVoiceChangerRecordings(prev => {
          const updated = [...prev, newVoiceChangerRecording];
          return updated;
        });
        
        // Auto-select the new recording for voice changer
        setSelectedVoiceChangerRecording(newVoiceChangerRecording);
        
        // Auto-select the first target voice if none is selected
        if (stockVoices.length > 0 && !selectedVoiceChangerVoice) {
          setSelectedVoiceChangerVoice(getFirstStockVoice());
        }
      }
      
      setCloneResult(`Voice clone created successfully: ${JSON.stringify(response, null, 2)}`);
      setSelectedVoiceId(response.voice_id);
    } catch (error) {
      setCloneResult(`Error creating voice clone: ${error.message}`);
    } finally {
      setCloneLoading(false);
    }
  };

  // -----------------------
  // Handlers for Synthesis
  // -----------------------
  const handleVoiceDropdownChange = (e) => {
    setSelectedVoiceId(e.target.value);
  };

  const handleSynthesisSourceChange = (e) => {
    setSynthInputSource(e.target.value);
    // Reset any previous synthesis sample.
    // setSynthesisText('');
    setSynthRecordingURL(null);
    setSynthUploadedFile(null);
  };

  const handleSynthFileChange = (e) => {
    setSynthUploadedFile(e.target.files[0]);
  };

  const handleSynthesize = async () => {
    if (!selectedVoiceId) {
      alert('Please select a voice.');
      return;
    }
    setSynthLoading(true);
    try {
      let audioResponse;
      if (synthInputSource === 'text') {
        if (!synthesisText) {
          alert('Please enter text to synthesize.');
          setSynthLoading(false);
          return;
        }
        const stream = await client.textToSpeech.convertAsStream(selectedVoiceId, {
          text: synthesisText,
          model_id: 'eleven_multilingual_v2',
        });
        const chunks = [];
        for await (const chunk of stream) {
          chunks.push(chunk);
        }
        audioResponse = new Blob(chunks, { type: 'audio/mpeg' });
      } else if (synthInputSource === 'record') {
        if (!synthRecordingURL) {
          alert('Please record your synthesis audio first.');
          setSynthLoading(false);
          return;
        }

        // For speech-to-speech conversion using recorded input. 
        // This was tricky as the webm format failed without much feedback
        // also, the conversionStream comes back in chunks
        const res = await fetch(synthRecordingURL);
        const blob = await res.blob();
        const fileForSynthesis = new File([blob], 'synth_input.mp3', { type: 'audio/mpeg' });
        const conversionStream = await client.speechToSpeech.convert(selectedVoiceId, {
          audio: fileForSynthesis,
          outputFormat: "mp3_44100_128",
          modelId: "eleven_multilingual_sts_v2"
        });
        const conversionChunks = [];
        for await (const chunk of conversionStream) {
          conversionChunks.push(chunk);
        }
        audioResponse = new Blob(conversionChunks, { type: 'audio/mpeg' });
      } else if (synthInputSource === 'file') {
        if (!synthUploadedFile) {
          alert('Please select a local MP3 file for synthesis.');
          setSynthLoading(false);
          return;
        }
        const conversionStream = await client.speechToSpeech.convert(selectedVoiceId, {
          audio: synthUploadedFile,
          outputFormat: "mp3_44100_128",
          modelId: "eleven_multilingual_sts_v2"
        });
        const conversionChunks = [];
        for await (const chunk of conversionStream) {
          conversionChunks.push(chunk);
        }
        audioResponse = new Blob(conversionChunks, { type: 'audio/mpeg' });
      }
      const url = URL.createObjectURL(audioResponse);

      // Get the correct voice name based on whether it's a stock voice or cloned voice
      const voiceName = useStockVoice ? selectedStockVoice?.name : getVoiceNameFromId(selectedVoiceId);

      // Add url to array of synth urls
      const new_synth = [...audioUrlSynth, {'id': selectedVoiceId, 'name': voiceName, 'url': url}];
      setAudioUrlSynth(new_synth);

      const audioBase64 = await blobToBase64(audioResponse);

      const save_blob = {
        'id': selectedVoiceId,
        'name': voiceName,
        'audio_blob': audioBase64
      }
      
      // Append the new synth to the existing synth array from local storage
      const storedVoicesData = localStorage.getItem('voices');
      if (storedVoicesData) {
        const parsedData = JSON.parse(storedVoicesData);
        const existingSynth = parsedData.synth || [];
        const new_synth = [...existingSynth, save_blob];
        saveSynthToLocalStorage(new_synth);
      } else {
        saveSynthToLocalStorage([save_blob]);
      }
      
      // Mark synthesis as completed and show voice changer section
      setSynthesisCompleted(true);
      saveSynthesisCompletedToLocalStorage(true);
      setShowVoiceChanger(true);
      
      // Auto-select the first target voice
      if (stockVoices.length > 0) {
        setSelectedVoiceChangerVoice(getFirstStockVoice());
      }
    } catch (err) {
      setError(err.message);
      console.error('Error synthesizing speech:', err);
    } finally {
      setSynthLoading(false);
    }
  };

  let cloneVoiceButton = <></>

  if (audioURL || uploadedFile) {
    cloneVoiceButton = (
      <>
        <br />
        <Button variant="success" type="submit" className="mt-4 btn-voice-clone" disabled={cloneLoading} style={{ height: '50px', width: '200px' }}>
          {cloneLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Cloning...
            </>
          ) : 'Clone Voice'}
        </Button>
      </>
    )
  }

  let audioSynthOut = []

  if (audioUrlSynth) {
    audioSynthOut = audioUrlSynth.map((data, index) => {
      return (
        <div key={index} className="mt-3">
          <div className="d-flex align-items-center gap-3">
            <Form.Label className="mb-0" style={{ minWidth: '120px' }}>{data.name}</Form.Label>
            <audio controls src={data.url} className="flex-grow-1 voice-cloning-audio" />
          </div>
        </div>
      )
    })
  }

  // Update synthesis visibility when voices change
  useEffect(() => {
    if (voices.length > 0 || (useStockVoice && selectedStockVoice)) {
      setSynthesisVisible(true);
    } else {
      setSynthesisVisible(false);
    }
  }, [voices, useStockVoice, selectedStockVoice]);

  // -----------------------
  // Utility functions for audio blob handling
  // -----------------------
  const audioURLToBase64 = async (audioURL) => {
    const response = await fetch(audioURL);
    const audioBlob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(audioBlob);
    });
  };

  const blobToBase64 = async (blob) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const base64ToBlob = (base64Data) => {
    // Strip off the data URL prefix if needed
    const byteString = atob(base64Data.split(',')[1]);
    const byteArray = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      byteArray[i] = byteString.charCodeAt(i);
    }
    return new Blob([byteArray]);
  };

  const base64ToAudioURL = (base64Data) => {
    const blob = base64ToBlob(base64Data);
    return URL.createObjectURL(blob);
  };

  const processAudioRecordingsFromStorage = (recordings) => {
    return recordings.map(recording => {
      if (recording.audio_blob) {
        return {
          ...recording,
          url: base64ToAudioURL(recording.audio_blob)
        };
      }
      return recording;
    });
  };

  // -----------------------
  // Render Combined Component
  // -----------------------
  return (
    <Container className="mt-4" style={{ marginBottom: "5px" }}>
      <Row>
        {/* Voice Clone Creation Section */}
        <Col md={12}>
          <Card className="mb-4 shadow-sm">
            <Card.Header as="h4">
              Create a Voice Clone
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleCloneSubmit}>
                <Row>
                  {/* Left Column - Record/Upload Voice */}
                  <Col md={showStockVoices ? 6 : 12}>
                    <h5>Record Your Own Voice</h5>
                <Form.Group controlId="source" className="mt-3">
                  <div style={{ marginTop: '10px' }}>
                    {selectedSource === "record" && <Testimonials voiceOver={props.voiceOver} />}
                  </div>
                </Form.Group>
                {selectedSource === 'record' ? (
                  <>
                        <MicRecorder onAudioURLChange={setAudioURL} showStockButton={!showStockVoices} onShowStock={() => {
                          setShowStockVoices(true);
                          saveShowStockVoicesToLocalStorage(true);
                        }} />
                    {audioURL && <audio controls src={audioURL} className="voice-cloning-audio" />}
                  </>
                ) : (
                  <Form.Group controlId="fileUpload" className="mt-3">
                    <Form.Label>Upload MP3 File</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".mp3,audio/mpeg"
                      onChange={handleFileChange}
                      required
                    />
                    {uploadedFile && (
                      <Form.Text className="text-muted">{uploadedFile.name}</Form.Text>
                    )}
                  </Form.Group>
                )}

                    {/* Clone Voice Button - Above Cloned Voices */}
                {cloneVoiceButton}

                    {/* Cloned Voices Section - Below Recording */}
                    {voices.length > 0 && (
                      <div className="mt-4">
                        <h5>Your Cloned Voices</h5>
                        <p className="text-muted mb-3">Click on a voice to select it for use</p>
                        <div className="d-flex flex-column gap-3">
                          {voices.map((voice) => (
                            <div 
                              key={voice.id} 
                              className={`p-3 border rounded ${selectedVoiceId === voice.id && !useStockVoice ? 'border-primary bg-light' : 'border-secondary'}`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleClonedVoiceSelect(voice.id)}
                            >
                              <div className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                  <h6 className="fw-bold mb-1">{voice.name}</h6>
                                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                    Your cloned voice
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Col>

                  {/* Right Column - Stock Voices (Hidden by default) */}
                  {showStockVoices && (
                    <Col md={6}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Select a Stock Voice</h5>
                        <Button 
                          variant="outline-secondary" 
                          size="sm" 
                          onClick={handleHideStockVoices}
                          style={{ fontSize: '0.8rem' }}
                        >
                          ×
                        </Button>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        {shuffleArray(stockVoices.filter(voice => voice.isNarrator)).map((voice) => (
                          <div 
                            key={voice.id} 
                            className={`p-2 border rounded ${selectedStockVoice?.id === voice.id ? 'border-primary bg-light' : 'border-light'}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleStockVoiceSelect(voice)}
                          >
                            <VoiceControl
                              voice={voice}
                              variant="outline-secondary"
                              size="sm"
                              onPlay={() => handleStockVoiceSelect(voice)}
                            />
                          </div>
                        ))}
                      </div>
                    </Col>
                  )}
                </Row>
              </Form>
              {(cloneResult && cloneResult.startsWith('Error')) && (
                <Alert variant={cloneResult.startsWith('Error') ? 'danger' : 'success'} className="mt-3">
                  <pre className="mb-0">{cloneResult}</pre>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        {/* Synthesis Section */}
        <Col md={12} style={{ display: synthesisVisible ? 'block' : 'none' }}>
          <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-success text-white">
              <h4>Voice Synthesis</h4>
            </Card.Header>
            <Card.Body>
              <Form>
                {/* Voice selection is now handled by the section above */}
                {/* <Form.Group controlId="voiceSelect">
                  <Form.Label>Select a Voice</Form.Label>
                  <Form.Control as="select" value={selectedVoiceId} onChange={handleVoiceDropdownChange}>
                    {voices.map((voice) => (
                      <option key={voice.id} value={voice.id}>
                        {voice.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group> */}
                
                {/* Display selected voice */}
                {selectedVoiceId && (
                  <div className="mb-3">
                    <p className="text-muted mb-0">
                      <strong>Selected Voice:</strong> {useStockVoice ? selectedStockVoice?.name : getVoiceNameFromId(selectedVoiceId)}
                    </p>
                  </div>
                )}
                
                {/* <Form.Group controlId="synthSource" className="mt-3">
                  <Form.Label>Synthesis Input Source</Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      label="Text Input"
                      name="synthSource"
                      value="text"
                      checked={synthInputSource === 'text'}
                      onChange={handleSynthesisSourceChange}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label="Record Input"
                      id="record-input"
                      value="record"
                      checked={synthInputSource === 'record'}
                      onChange={handleSynthesisSourceChange}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label="Upload MP3 File"
                      id="upload-mp3-input"
                      value="file"
                      checked={synthInputSource === 'file'}
                      onChange={handleSynthesisSourceChange}
                    />
                  </div>
                </Form.Group> */}
                {synthInputSource === 'text' && (
                  <Form.Group controlId="textInput" className="mt-3">
                    <Form.Control
                      as="textarea"
                      rows={4}
                      placeholder={props.workshopConfig.placeholder}
                      value={synthesisText}
                      onChange={(e) => setSynthesisText(e.target.value)}
                      maxLength={1000}
                    />
                  </Form.Group>
                )}
                {synthInputSource === 'record' && (
                  <Form.Group controlId="synthMicInput" className="mt-3">
                    <Form.Label>Record Input</Form.Label>
                    <MicRecorder onAudioURLChange={setSynthRecordingURL} />
                    {synthRecordingURL && <audio controls src={synthRecordingURL} className="voice-cloning-audio" />}
                  </Form.Group>
                )}
                {synthInputSource === 'file' && (
                  <Form.Group controlId="synthFileInput" className="mt-3">
                    <Form.Label>Select Local MP3 File</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".mp3,audio/mpeg"
                      onChange={handleSynthFileChange}
                    />
                    {synthUploadedFile && (
                      <Form.Text className="text-muted">{synthUploadedFile.name}</Form.Text>
                    )}
                  </Form.Group>
                )}
                <Button variant="primary" onClick={handleSynthesize} className="mt-4 btn-voice-synthesize" disabled={synthLoading}>
                  {synthLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Synthesizing...
                    </>
                  ) : 'Synthesize and Play'}
                </Button>
              </Form>
              {audioSynthOut}
              {error && <div>{error}</div>}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Voice Changer Section */}
      {showVoiceChanger && (
        <Row>
          <Col md={12}>
            <Card className="mb-4 shadow-sm">
              <Card.Header as="h4">
                Voice Changer
              </Card.Header>
              <Card.Body>
                <Row>
                  {/* Left Column - Recordings List and Recording Interface */}
                  <Col md={6}>
                    <h5>Input Recordings</h5>
                    
                    {/* Input Recordings List */}
                    {voiceChangerInputs.length > 0 && (
                      <div className="mb-4">
                        <div className="d-flex flex-column gap-2">
                          {voiceChangerInputs.map((recording) => (
                            <div 
                              key={recording.id}
                              className={`p-3 border rounded ${selectedVoiceChangerRecording?.id === recording.id ? 'border-primary bg-light' : 'border-light'}`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setSelectedVoiceChangerRecording(recording)}
                            >
                              <div className="d-flex align-items-center gap-3">
                                <Form.Check
                                  type="radio"
                                  id={`recording-${recording.id}`}
                                  name="voiceChangerRecording"
                                  checked={selectedVoiceChangerRecording?.id === recording.id}
                                  onChange={() => setSelectedVoiceChangerRecording(recording)}
                                  className="mb-0"
                                />
                                <Form.Label 
                                  className="mb-0" 
                                  style={{ minWidth: '30px', cursor: 'pointer' }}
                                  onClick={() => setSelectedVoiceChangerRecording(recording)}
                                >
                                  {recording.name}
                                </Form.Label>
                                <audio controls src={recording.url} className="flex-grow-1 voice-cloning-audio" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Always Show Recording Interface */}
                    <hr />
                    <div>
                      <h6>Record New Audio</h6>
                      <MicRecorder onAudioURLChange={handleAddVoiceChangerRecording} />
                    </div>

                    <hr />

                    {/* Change Voice Button - Moved to left column below recording */}
                    <div className="mt-3">
                      <OverlayTrigger
                        placement="bottom"
                        overlay={
                          <Popover id="change-voice-tooltip">
                            <Popover.Body>
                              {!selectedVoiceChangerRecording && !selectedVoiceChangerVoice && (
                                <div>Record an audio file and select a target voice to enable voice changing.</div>
                              )}
                              {!selectedVoiceChangerRecording && selectedVoiceChangerVoice && (
                                <div>Record an audio file to enable voice changing.</div>
                              )}
                              {selectedVoiceChangerRecording && !selectedVoiceChangerVoice && (
                                <div>Select a target voice to enable voice changing.</div>
                              )}
                            </Popover.Body>
                          </Popover>
                        }
                        show={(!selectedVoiceChangerVoice || !selectedVoiceChangerRecording) ? undefined : false}
                      >
                        <span>
                          <Button 
                            variant="warning" 
                            onClick={handleVoiceChanger}
                            disabled={!selectedVoiceChangerVoice || !selectedVoiceChangerRecording || voiceChangerLoading}
                            size="lg"
                            className="btn-voice-changer"
                          >
                            {voiceChangerLoading ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Changing Voice...
                              </>
                            ) : 'Change Voice'}
                          </Button>
                        </span>
                      </OverlayTrigger>
                    </div>

                    {/* Output Recordings Section */}
                    {voiceChangerOutputs.length > 0 && (
                      <div className="mt-4">
                        <h5>Voice-changed recordings</h5>
                        <div className="d-flex flex-column gap-3">
                          {voiceChangerOutputs.map((recording) => (
                            <div 
                              key={recording.id}
                              className="p-3 border rounded border-success bg-light"
                            >
                              <div className="mb-2 d-flex justify-content-between align-items-start">
                                <div>
                                  <h6 className="mb-1">Recording {recording.name}</h6>
                                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                    Converted from "{recording.originalVoice}" to "{recording.targetVoice}"
                                  </p>
                                </div>
                                <Button 
                                  variant="outline-secondary" 
                                  size="sm"
                                  onClick={() => handleSubmitToSlideshow(recording)}
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                  disabled={submittedToSlideshow === recording.id}
                                  className="btn-voice-submit"
                                >
                                  {submittedToSlideshow === recording.id ? 'Submitted' : 'Submit to slideshow'}
                                </Button>
                              </div>
                              <audio controls src={recording.url} className="w-100 voice-cloning-audio" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Col>

                  {/* Right Column - Stock Voice Selection */}
                  <Col md={6}>
                    <h5>Select Target Voice</h5>
                    <p className="text-muted mb-3">Choose a voice to change your audio to</p>
                    
                    {/* Characters Section */}
                    <div className="mb-4">
                      <h6 className="mb-2">Characters</h6>
                      <div className="d-flex flex-column gap-1">
                        {shuffleArray(stockVoices.filter(voice => voice.category === 'Characters')).map((voice) => (
                          <div 
                            key={voice.id} 
                            className={`p-2 border rounded ${selectedVoiceChangerVoice?.id === voice.id ? 'border-primary bg-light' : 'border-light'}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleVoiceChangerVoiceSelect(voice)}
                          >
                            <VoiceControl
                              voice={voice}
                              variant="outline-secondary"
                              size="sm"
                              onPlay={() => handleVoiceChangerVoiceSelect(voice)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Villains Section */}
                    <div className="mb-4">
                      <h6 className="mb-2">Villains</h6>
                      <div className="d-flex flex-column gap-1">
                        {shuffleArray(stockVoices.filter(voice => voice.category === 'Villains')).map((voice) => (
                          <div 
                            key={voice.id} 
                            className={`p-2 border rounded ${selectedVoiceChangerVoice?.id === voice.id ? 'border-primary bg-light' : 'border-light'}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleVoiceChangerVoiceSelect(voice)}
                          >
                            <VoiceControl
                              voice={voice}
                              variant="outline-secondary"
                              size="sm"
                              onPlay={() => handleVoiceChangerVoiceSelect(voice)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Fantasy Section */}
                    <div className="mb-4">
                      <h6 className="mb-2">Fantasy</h6>
                      <div className="d-flex flex-column gap-1">
                        {shuffleArray(stockVoices.filter(voice => voice.category === 'Fantasy')).map((voice) => (
                          <div 
                            key={voice.id} 
                            className={`p-2 border rounded ${selectedVoiceChangerVoice?.id === voice.id ? 'border-primary bg-light' : 'border-light'}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleVoiceChangerVoiceSelect(voice)}
                          >
                            <VoiceControl
                              voice={voice}
                              variant="outline-secondary"
                              size="sm"
                              onPlay={() => handleVoiceChangerVoiceSelect(voice)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Narrators Section */}
                    <div className="mb-4">
                      <h6 className="mb-2">Narrators</h6>
                      <div className="d-flex flex-column gap-1">
                        {shuffleArray(stockVoices.filter(voice => voice.category === 'Narrators')).map((voice) => (
                          <div 
                            key={voice.id} 
                            className={`p-2 border rounded ${selectedVoiceChangerVoice?.id === voice.id ? 'border-primary bg-light' : 'border-light'}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleVoiceChangerVoiceSelect(voice)}
                          >
                            <VoiceControl
                              voice={voice}
                              variant="outline-secondary"
                              size="sm"
                              onPlay={() => handleVoiceChangerVoiceSelect(voice)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {showVoiceChanger && (
        <>
          <hr style={{ marginTop: "20px" }} />
          <div style={{
            backgroundColor: "white",
            fontSize: "large",
            border: "2px solid #f05f40",
            borderRadius: "15px",
            padding: "10px 15px 15px 15px",
            margin: "15px 0 10px 0",
            boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)",
            width: "50%"
          }}>
            <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
              Instructions:
            </h3>
            <span style={{ margin: "0", lineHeight: "2.2" }}>
              {props.workshopConfig.instructionText}{" "}
              <Button
                className="btn-nav-link"
                onClick={() => props.switchTab('gpt')}
                style={{
                  margin: "0 5px",
                  padding: "4px 12px",
                  fontSize: "14px",
                  display: "inline",
                  whiteSpace: "nowrap",
                  backgroundColor: "white",
                  borderColor: "#f05f40",
                  color: "#f05f40",
                  verticalAlign: "middle",
                }}
              >
                {props.workshopConfig.nextStepButtonText}
              </Button>
            </span>
          </div>
          <hr style={{ marginTop: "15px" }} />
        </>
      )}

      <Button
        onClick={scrollTop}
        style={{
          display: showScroll ? "flex" : "none",
          position: "fixed",
          bottom: "80px",
          right: "20px",
          zIndex: "1000",
        }}
        variant="secondary"
      >
        ↑
      </Button>
      <Button
        onClick={scrollBottom}
        style={{
          display: showScrollBottom ? "flex" : "none",
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: "1000",
        }}
        variant="secondary"
      >
        ↓
      </Button>
      
    </Container>
  );
};

export { VoiceCloneCreatorOptions };
